import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams, NavLink } from "react-router-dom";
import ContactForm from "@/components/ContactForm";

function useHashScroll() {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          const y = el.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: y, behavior: "smooth" });
        }, 60);
      }
    }
  }, [hash, pathname]);
}

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className={`fp-faq__item ${open ? "is-open" : ""}`}>
      <button type="button" className="fp-faq__q" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span>{q}</span>
        <span className="fp-faq__icon" aria-hidden="true">{open ? "–" : "+"}</span>
      </button>
      {open && <div className="fp-faq__a">{a}</div>}
    </div>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const base = `/${lang || "ar"}`;
  useHashScroll();

  const howSteps = t("how.steps", { returnObjects: true }) || [];
  const benefits = t("benefits.items", { returnObjects: true }) || [];
  const faqs = t("faq.items", { returnObjects: true }) || [];

  return (
    <div className="fp-home" id="top">
      {/* ============== HERO ============== */}
      <section className="fp-hero">
        <div className="fp-hero__grid">
          <div className="fp-hero__copy">
            <span className="fp-eyebrow">{t("hero.eyebrow")}</span>
            <h1 className="fp-hero__title">
              {t("hero.title")}
            </h1>
            <p className="fp-hero__subtitle">{t("hero.subtitle")}</p>
            <div className="fp-hero__cta">
              <a href="#contact" className="fp-btn fp-btn--primary">{t("hero.primary_cta")} <span aria-hidden="true">→</span></a>
              <a href="#how" className="fp-btn fp-btn--ghost">{t("hero.secondary_cta")}</a>
            </div>

            <div className="fp-hero__stats">
              <div className="fp-stat">
                <span className="fp-stat__value">{t("hero.stat_1_value")}</span>
                <span className="fp-stat__label">{t("hero.stat_1_label")}</span>
              </div>
              <div className="fp-stat">
                <span className="fp-stat__value">{t("hero.stat_2_value")}</span>
                <span className="fp-stat__label">{t("hero.stat_2_label")}</span>
              </div>
              <div className="fp-stat">
                <span className="fp-stat__value">{t("hero.stat_3_value")}</span>
                <span className="fp-stat__label">{t("hero.stat_3_label")}</span>
              </div>
            </div>
          </div>

          <div className="fp-hero__visual">
            <div className="fp-hero__panel">
              <div className="fp-hero__panelHead">
                <div className="fp-hero__dots"><i /><i /><i /></div>
                <span className="fp-hero__panelTitle">{t("hero.panel_title")}</span>
              </div>
              <div className="fp-hero__panelBody">
                <div className="fp-hero__chartRow">
                  <div className="fp-hero__bar" style={{ height: "38%" }}><span>A</span></div>
                  <div className="fp-hero__bar fp-hero__bar--accent" style={{ height: "66%" }}><span>B</span></div>
                  <div className="fp-hero__bar" style={{ height: "52%" }}><span>C</span></div>
                  <div className="fp-hero__bar" style={{ height: "78%" }}><span>D</span></div>
                  <div className="fp-hero__bar fp-hero__bar--warm" style={{ height: "44%" }}><span>E</span></div>
                  <div className="fp-hero__bar" style={{ height: "61%" }}><span>F</span></div>
                </div>
                <div className="fp-hero__cards">
                  <div className="fp-hero__miniCard">
                    <span className="fp-hero__miniLabel">{t("hero.panel_overview")}</span>
                    <span className="fp-hero__miniVal">{t("hero.panel_overview_val")}</span>
                    <span className="fp-hero__pill">{t("hero.panel_overview_pill")}</span>
                  </div>
                  <div className="fp-hero__miniCard fp-hero__miniCard--dark">
                    <span className="fp-hero__miniLabel">{t("hero.panel_sources")}</span>
                    <span className="fp-hero__miniVal">42</span>
                    <span className="fp-hero__pill fp-hero__pill--warm">{t("hero.panel_sources_pill")}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="fp-hero__badge">
              <span>→</span> BA · AR
            </div>
          </div>
        </div>
      </section>

      {/* ============== ABOUT ============== */}
      <section className="fp-section fp-about" id="about">
        <div className="fp-section__head">
          <span className="fp-eyebrow">{t("about.eyebrow")}</span>
          <h2 className="fp-section__title">{t("about.title")}</h2>
          <p className="fp-section__lead">{t("about.lead")}</p>
        </div>
        <div className="fp-about__grid">
          <div className="fp-about__image">
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80"
              alt={t("about.image_alt")}
              loading="lazy"
            />
            <div className="fp-about__imgTag">{t("hero.dashboard_tag")}</div>
          </div>
          <div className="fp-about__cards">
            <article className="fp-card">
              <span className="fp-card__num">01</span>
              <h3>{t("about.card_1_title")}</h3>
              <p>{t("about.card_1_body")}</p>
            </article>
            <article className="fp-card fp-card--accent">
              <span className="fp-card__num">02</span>
              <h3>{t("about.card_2_title")}</h3>
              <p>{t("about.card_2_body")}</p>
            </article>
            <article className="fp-card">
              <span className="fp-card__num">03</span>
              <h3>{t("about.card_3_title")}</h3>
              <p>{t("about.card_3_body")}</p>
            </article>
          </div>
        </div>
      </section>

      {/* ============== HOW ============== */}
      <section className="fp-section fp-how" id="how">
        <div className="fp-section__head">
          <span className="fp-eyebrow">{t("how.eyebrow")}</span>
          <h2 className="fp-section__title">{t("how.title")}</h2>
          <p className="fp-section__lead">{t("how.lead")}</p>
        </div>
        <ol className="fp-how__steps">
          {Array.isArray(howSteps) && howSteps.map((s, i) => (
            <li className="fp-how__step" key={i}>
              <span className="fp-how__num">{s.n}</span>
              <div>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ============== BENEFITS ============== */}
      <section className="fp-section fp-benefits" id="benefits">
        <div className="fp-benefits__wrap">
          <div className="fp-benefits__side">
            <span className="fp-eyebrow fp-eyebrow--light">{t("benefits.eyebrow")}</span>
            <h2 className="fp-section__title fp-section__title--light">{t("benefits.title")}</h2>
            <div className="fp-benefits__photo">
              <img
                src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=900&q=80"
                alt={t("about.image_alt")}
                loading="lazy"
              />
            </div>
          </div>
          <ul className="fp-benefits__list">
            {Array.isArray(benefits) && benefits.map((b, i) => (
              <li key={i}>
                <span className="fp-benefits__check">✓</span>
                <div>
                  <h3>{b.title}</h3>
                  <p>{b.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="fp-disclaimer">
          <h3>{t("disclaimer.title")}</h3>
          <p>{t("disclaimer.body")}</p>
        </div>
      </section>

      {/* ============== FORM ============== */}
      <section className="fp-section fp-contact" id="contact">
        <div className="fp-contact__grid">
          <div className="fp-contact__copy">
            <span className="fp-eyebrow">{t("form.eyebrow")}</span>
            <h2 className="fp-section__title">{t("form.title")}</h2>
            <p className="fp-section__lead">{t("form.subtitle")}</p>
            <div className="fp-contact__photo">
              <img
                src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=900&q=80"
                alt={t("hero.image_alt")}
                loading="lazy"
              />
            </div>
          </div>
          <div className="fp-contact__form">
            <ContactForm />
          </div>
        </div>
      </section>

      {/* ============== FAQ ============== */}
      <section className="fp-section fp-faq" id="faq">
        <div className="fp-section__head">
          <span className="fp-eyebrow">{t("faq.eyebrow")}</span>
          <h2 className="fp-section__title">{t("faq.title")}</h2>
        </div>
        <div className="fp-faq__list">
          {Array.isArray(faqs) && faqs.map((f, i) => (
            <FaqItem key={i} q={f.q} a={f.a} idx={i} />
          ))}
        </div>
      </section>

      {/* ============== POLICIES TEASER ============== */}
      <section className="fp-section fp-policies" id="policies">
        <div className="fp-policies__inner">
          <div>
            <span className="fp-eyebrow">{t("policies_teaser.eyebrow")}</span>
            <h2 className="fp-section__title">{t("policies_teaser.title")}</h2>
            <p className="fp-section__lead">{t("policies_teaser.body")}</p>
          </div>
          <div className="fp-policies__links">
            <NavLink to={base + "/privacy"} className="fp-policies__link">{t("policies_teaser.privacy")} <span>→</span></NavLink>
            <NavLink to={base + "/cookies"} className="fp-policies__link">{t("policies_teaser.cookies")} <span>→</span></NavLink>
            <NavLink to={base + "/terms"} className="fp-policies__link">{t("policies_teaser.terms")} <span>→</span></NavLink>
          </div>
        </div>
      </section>
    </div>
  );
}
